import React, {
  CSSProperties,
  FC,
  RefObject,
  useCallback,
  useState,
} from 'react';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, firebaseAuth } from '../../../../shared/lib';
import { LoggerService } from '../../../../shared/logger';
import { IconButton, TextField, styled } from '@mui/material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useLocation } from 'react-router-dom';
import { IN_PLAY_ROUTE } from '../../in-play';
import { useAlert } from '@/shared/components';
import { isBlockedBetween } from '../hooks';
import {
  decrementChatQuota,
  decrementGroupChatQuota,
  getDmChatPaymentRequirement,
  useChatQuota,
  useGroupChatQuota,
  usePurchaseChatQuota,
  usePurchaseGroupChatQuota,
  CHAT_ENTRY_FEE,
} from '../hooks/use-chat-quota';
import { upsertChatThreadSummary } from '../hooks/use-chat-threads';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import {
  CoinPile,
  NotEnoughCoins,
  useNotEnoughCoins,
} from '@/shared/components';

export interface SendMessageProps {
  scroll: RefObject<HTMLSpanElement>;
  receiverID?: string;
  roomID?: string;
  messageFormProps?: CSSProperties;
  /**
   * Forces the input into a read-only "blocked" state. The send-message
   * component also re-checks the block status on submit, but a live
   * useIsBlocked hook in the parent supplies this for instant UI feedback.
   */
  disabledReason?: 'i-blocked' | 'blocked-me' | null;
  /**
   * Firebase UIDs of every member allowed to read/write this group chat
   * (i.e. everyone currently in the in-play room, including me). Required
   * for group-chat writes — Firestore rules enforce that the caller is in
   * this array. Ignored for DMs.
   */
  allowedUIDs?: string[];
}

export const SendMessage: FC<SendMessageProps> = ({
  scroll,
  receiverID = '',
  roomID = '',
  messageFormProps = {},
  disabledReason = null,
  allowedUIDs = [],
}) => {
  const [message, setMessage] = useState('');
  const [user] = useAuthState(firebaseAuth);
  const { pathname } = useLocation();
  const { addAlert } = useAlert();
  const isGroupChat = pathname === IN_PLAY_ROUTE.ABSOLUTE_PATH;
  const isDisabled = !isGroupChat && disabledReason !== null;

  // Pay-to-chat quotas — DM uses /chat_quotas, group uses /group_chat_quotas.
  const dmReceiverID = !isGroupChat ? receiverID : '';
  const groupRoomID = isGroupChat ? roomID : '';
  const { hasQuota: dmHasQuota } = useChatQuota(dmReceiverID || null);
  const { hasQuota: groupHasQuota } = useGroupChatQuota(groupRoomID || null);
  const hasQuota = isGroupChat ? groupHasQuota : dmHasQuota;
  const {
    showCoinsMessage,
    setShowCoinsMessage,
    hasEnoughCoins,
    checkIfEnoughCoins,
  } = useNotEnoughCoins();
  const { purchase: purchaseDm, isLoading: purchasingDm } =
    usePurchaseChatQuota(dmReceiverID || null);
  const { purchase: purchaseGroup, isLoading: purchasingGroup } =
    usePurchaseGroupChatQuota(groupRoomID || null);
  const purchase = isGroupChat ? purchaseGroup : purchaseDm;
  const purchasing = isGroupChat ? purchasingGroup : purchasingDm;
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const placeholder = (() => {
    if (disabledReason === 'i-blocked') {
      return '🚫 Unblock this user to send a message';
    }
    if (disabledReason === 'blocked-me') {
      return '🚫 You can’t reply to this conversation';
    }
    if (dmReceiverID && !hasQuota) {
      return '🪙 Unlock this 100-message chat window';
    }
    if (isGroupChat && groupRoomID && !hasQuota) {
      return `🪙 Pay ${CHAT_ENTRY_FEE} coins to chat in this room`;
    }
    return '📝 Type your message...';
  })();

  const handleConfirmPurchase = useCallback(
    async (messageOverride?: string) => {
      const messageToSend = messageOverride ?? pendingMessage;
      try {
        await purchase();
        setPurchaseOpen(false);
        if (messageToSend && user) {
          const { uid, displayName, photoURL } = user;
          if (isGroupChat && roomID) {
            await addDoc(collection(db, 'chat'), {
              message: messageToSend,
              groupID: roomID,
              name: displayName,
              avatar: photoURL,
              createdAt: serverTimestamp(),
              senderID: uid,
              receiverID: '',
              isRead: 'false',
              threadID: [],
              allowedUIDs,
            });
            setMessage('');
            decrementGroupChatQuota(roomID, uid);
          } else if (receiverID) {
            await addDoc(collection(db, 'chat'), {
              message: messageToSend,
              groupID: '',
              name: displayName,
              avatar: photoURL,
              createdAt: serverTimestamp(),
              senderID: uid,
              receiverID,
              isRead: 'false',
              threadID: [uid, receiverID],
              allowedUIDs: [],
            });
            setMessage('');
            // Decrement so the count is consistent after the very first
            // post-purchase send.
            decrementChatQuota(uid, receiverID);
            upsertChatThreadSummary(uid, receiverID, messageToSend);
          }
          setPendingMessage(null);
        }
      } catch (err: unknown) {
        LoggerService.error('Chat quota purchase failed', err);
        // The server's "insufficient coins" 400 is the most common cause —
        // surface that via the existing wheel-spin prompt so the user has a
        // clear way out instead of a vague toast.
        const errStr = ((): string => {
          if (err && typeof err === 'object') {
            // axios-like shape
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const e = err as any;
            const fromBody =
              e?.response?.data?.error ?? e?.data?.error ?? e?.message;
            if (typeof fromBody === 'string') return fromBody;
          }
          return '';
        })();
        if (/insufficient coins/i.test(errStr)) {
          setPurchaseOpen(false);
          checkIfEnoughCoins();
          return;
        }
        addAlert({
          type: 'error',
          message: 'Could not start the chat. Please try again in a moment.',
        });
      }
    },
    [
      pendingMessage,
      purchase,
      user,
      isGroupChat,
      roomID,
      allowedUIDs,
      receiverID,
      checkIfEnoughCoins,
      addAlert,
    ]
  );

  const sendMessage = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (message.trim() === '' || !user) {
        LoggerService.info('Message is empty');
        return;
      }
      const { uid, displayName, photoURL } = user;

      // For DMs, double-check the block state against Firestore directly
      // — the parent-supplied `disabledReason` is the source of truth for
      // the UI, but a race between two devices is still possible.
      if (!isGroupChat && receiverID) {
        try {
          const { aBlockedB, bBlockedA } = await isBlockedBetween(
            uid,
            receiverID
          );
          if (aBlockedB) {
            addAlert({
              type: 'warning',
              message: 'Unblock this user to send a message.',
            });
            return;
          }
          if (bBlockedA) {
            addAlert({
              type: 'warning',
              message: 'You can’t send messages to this user.',
            });
            return;
          }
        } catch (err) {
          LoggerService.error('block-state pre-check failed', err);
          // If the check itself fails we conservatively block the send so we
          // never leak a message through.
          addAlert({
            type: 'error',
            message: 'Could not verify chat status. Please try again.',
          });
          return;
        }
      }

      if (isGroupChat) {
        // Defensive: if the parent forgot to pass allowedUIDs, refuse to write
        // rather than create a message that the rules will reject anyway.
        if (!allowedUIDs.includes(uid)) {
          LoggerService.error(
            'Group-chat write attempted without my uid in allowedUIDs',
            { uid, allowedUIDs }
          );
          addAlert({
            type: 'error',
            message: 'Could not send message — room membership not loaded yet.',
          });
          return;
        }
      }

      // Pay-to-chat gate (DM + group). Require a quota doc with remaining
      // > 0 before writing. For DMs, purchase() only charges coins for a
      // brand-new conversation or after the current 100-message window is
      // exhausted; first replies in an existing conversation simply restore
      // the remaining window and continue.
      if (!isGroupChat && receiverID) {
        const { shouldCharge } = await getDmChatPaymentRequirement(
          uid,
          receiverID
        );
        if (shouldCharge) {
          setPendingMessage(message);
          if (!hasEnoughCoins) {
            checkIfEnoughCoins();
            return;
          }
          setPurchaseOpen(true);
          return;
        }
        if (!hasQuota) {
          setPendingMessage(message);
          await handleConfirmPurchase(message);
          return;
        }
      }

      const needsGroupPaywall = isGroupChat && groupRoomID && !hasQuota;
      if (needsGroupPaywall) {
        setPendingMessage(message);
        if (!hasEnoughCoins) {
          checkIfEnoughCoins();
          return;
        }
        setPurchaseOpen(true);
        return;
      }

      await addDoc(collection(db, 'chat'), {
        message,
        groupID: isGroupChat ? roomID : '',
        name: displayName,
        avatar: photoURL,
        createdAt: serverTimestamp(),
        senderID: uid,
        receiverID,
        isRead: 'false',
        threadID: isGroupChat ? [] : [uid, receiverID],
        allowedUIDs: isGroupChat ? allowedUIDs : [],
      });
      setMessage('');
      if (!isGroupChat && uid && receiverID) {
        // Best-effort: rules will reject the next send anyway if Firestore
        // hasn't synced. Worst case the count drifts by 1 — never a leak.
        decrementChatQuota(uid, receiverID);
        // Update the dedicated /chat_threads summary so the DM sidebar
        // can render this thread without scanning the last N messages.
        upsertChatThreadSummary(uid, receiverID, message);
      }
      if (isGroupChat && uid && roomID) {
        decrementGroupChatQuota(roomID, uid);
      }
    },
    [
      message,
      receiverID,
      roomID,
      user,
      isGroupChat,
      groupRoomID,
      addAlert,
      allowedUIDs,
      hasQuota,
      hasEnoughCoins,
      checkIfEnoughCoins,
      handleConfirmPurchase,
    ]
  );

  return (
    <>
      <SendMessageForm onSubmit={sendMessage} style={messageFormProps}>
        <TextField
          id="messageInput"
          name="messageInput"
          type="text"
          placeholder={placeholder}
          size="small"
          value={message}
          onChange={e => setMessage(e.target.value)}
          multiline
          maxRows={4}
          disabled={isDisabled}
          InputProps={{
            style: { height: `fit-content`, fontSize: 14 },
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: theme =>
                theme.palette.mode === 'dark'
                  ? 'rgba(55, 65, 81, 0.6)'
                  : 'rgba(243, 244, 246, 0.8)',
              backdropFilter: 'blur(8px)',
              border: theme =>
                `1px solid ${
                  theme.palette.mode === 'dark'
                    ? 'rgba(88, 166, 198, 0.3)'
                    : 'rgba(229, 230, 235, 0.6)'
                }`,
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: theme => theme.palette.primary.main,
              },
              '&.Mui-focused': {
                boxShadow: theme => `0 0 0 3px ${theme.palette.primary.main}30`,
                borderColor: theme => theme.palette.primary.main,
              },
            },
            '& .MuiOutlinedInput-input::placeholder': {
              opacity: 0.7,
            },
          }}
        />
        <IconButton
          type="submit"
          size="small"
          disabled={isDisabled}
          sx={{
            marginLeft: 1,
            background: theme =>
              isDisabled
                ? theme.palette.action.disabledBackground
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: 'white',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isDisabled ? 'none' : 'scale(1.05)',
              boxShadow: theme =>
                isDisabled
                  ? 'none'
                  : `0 4px 12px ${theme.palette.primary.main}60`,
            },
            '&:active': {
              transform: isDisabled ? 'none' : 'scale(0.95)',
            },
            '&.Mui-disabled': {
              color: theme => theme.palette.text.disabled,
            },
          }}
        >
          {isDisabled ? (
            <BlockIcon fontSize="small" />
          ) : (
            <SendOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </SendMessageForm>
      <Dialog
        open={purchaseOpen}
        onOpenChange={open => {
          setPurchaseOpen(open);
          if (!open) setPendingMessage(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CoinPile size="sm" />
              Pay to chat
            </DialogTitle>
            <DialogDescription>
              It costs <strong>{CHAT_ENTRY_FEE} coins</strong> to start a chat
              or unlock the next 100-message window. Replies within an active
              100-message conversation window are free.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setPurchaseOpen(false);
                setPendingMessage(null);
              }}
              disabled={purchasing}
            >
              Not now
            </Button>
            <Button
              onClick={() => handleConfirmPurchase()}
              disabled={purchasing}
            >
              {purchasing ? 'Unlocking…' : `Pay ${CHAT_ENTRY_FEE} coins`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NotEnoughCoins
        showCoinsMessage={showCoinsMessage}
        setShowCoinsMessage={setShowCoinsMessage}
      />
    </>
  );
};

export const SendMessageForm = styled('form')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(1),
  borderTop: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(88, 166, 198, 0.2)'
      : 'rgba(229, 230, 235, 0.5)'
  }`,
  backgroundColor: 'transparent',
}));
